import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { TabPanel } from 'primereact/tabview';
import { TabViewStyled } from 'styles/v2/Styles.styled';
import { EpdLinks } from 'routes/EpdRoutes';
import { CompilerCompositeStatusModel } from 'types/types';
import LcaResultsTab from './LcaResultsTab';
import ScenariosTab from './ScenariosTab';
import SpecificationTab from './SpecificationTab';
import SystemBoundaryTab from './SystemBoundaryTab';
import { CompilerFieldMatcher, getStateForFields } from './CompilerStepsValidator';
import StepTabHeader from './StepTabHeader';
import { compilerStep3Tabs } from './compilerNavConstants';

const specificationTabFields: CompilerFieldMatcher[] = [
  'infrastructureAndCapitalGoods',
  'dataQualityAssessment',
  'lcaSpecification',
  'epdDataSource',
  /^epdDataSource\[\d+\]$/,
  'datasourceSectionErrorDatabaseVersion',
  'otherDatabaseUsedAsDatasourceDescription',
  /^electricityItems\[\d+\]$/,
  'lcaSoftwareVersionId',
  'lcaSoftwareId',
  'otherSoftwareVersion',
  'otherSoftwareName',
  'referencePackageVersion15804',
  'technologyDescription',
  'scrapInputs',
  'epdShareOfTotalScrapInput',
  'scrapInputTotalPercent',
  'biogasData',
  'characterisationMethods',
];

const systemBoundaryTabFields: CompilerFieldMatcher[] = [
  'systemBoundaryDescription',
  'hasExcludedModules',
  'excludedModulesJustification',
  'declaredModulesErrors',
  'processFlowDiagramsImages',
];

const scenariosTabFields: CompilerFieldMatcher[] = ['scenarios'];

const environmentalPerformanceTabFields: CompilerFieldMatcher[] = [
  'lcaResults',
  'cO2Carbonation',
  'declarationOfCO2Assumptions',
  'transportationPerformance',
  'justificationForInclusion',
];

export type CustomResultValidateButtonPressed = {
  id: string;
  isValidateBtnPressed: boolean;
};

const Step3: React.FunctionComponent<{
  epdId: string;
  epdVersionId: string;
  tab: string | null | undefined;
  validationState: CompilerCompositeStatusModel | undefined;
}> = ({ epdId, epdVersionId, tab, validationState }) => {
  const history = useHistory();

  let tabIndex = compilerStep3Tabs.findIndex((t) => t === tab);
  if (tabIndex === -1) {
    tabIndex = 0;
  }

  const errors = validationState?.errors;

  const specificationTabState = getStateForFields(specificationTabFields, errors);
  const systemBoundaryTabState = getStateForFields(systemBoundaryTabFields, errors);
  const scenariosTabState = getStateForFields(scenariosTabFields, errors);
  const environmentalPerformanceTabState = getStateForFields(environmentalPerformanceTabFields, errors);

  const [isCore15804ValidateBtnPressed, setIsCore15804ValidateBtnPressed] = useState(false);
  const [isAdditionalMandatoryValidateBtnPressed, setIsAdditionalMandatoryValidateBtnPressed] = useState(false);
  const [isAdditionalVoluntary15804ValidateBtnPressed, setIsAdditionalVoluntary15804ValidateBtnPressed] = useState(false);
  const [isAdditionalVoluntaryValidateBtnPressed, setIsAdditionalVoluntaryValidateBtnPressed] = useState(false);
  const [isResource15804ValidateBtnPressed, setIsResource15804ValidateBtnPressed] = useState(false);
  const [isWaste15804ValidateBtnPressed, setIsWaste15804ValidateBtnPressed] = useState(false);
  const [isOutputFlow15804ValidateBtnPressed, setIsOutputFlow15804ValidateBtnPressed] = useState(false);
  const [customResultButtonPressed, setCustomResultButtonPressed] = useState<CustomResultValidateButtonPressed[]>([]);

  return (
    <TabViewStyled
      activeIndex={tabIndex}
      onTabChange={(e) =>
        history.push(EpdLinks.epdCompilerSpecific(epdId, { step: 'step3', tab: compilerStep3Tabs[e.index] }))
      }
    >
      <TabPanel header={<StepTabHeader title="Specification" state={specificationTabState} />}>
        <SpecificationTab epdId={epdId} epdVersionId={epdVersionId} validationState={validationState} />
      </TabPanel>
      <TabPanel header={<StepTabHeader title="System boundary" state={systemBoundaryTabState} />}>
        <SystemBoundaryTab epdId={epdId} epdVersionId={epdVersionId} validationState={validationState} />
      </TabPanel>
      <TabPanel header={<StepTabHeader title="Scenarios" state={scenariosTabState} />}>
        <ScenariosTab epdVersionId={epdVersionId} validationState={validationState} />
      </TabPanel>
      <TabPanel header={<StepTabHeader title="Environmental performance" state={environmentalPerformanceTabState} />}>
        <LcaResultsTab
          epdVersionId={epdVersionId}
          validationState={validationState}
          isCore15804ValidateBtnPressed={isCore15804ValidateBtnPressed}
          setIsCore15804ValidateBtnPressed={setIsCore15804ValidateBtnPressed}
          isAdditionalMandatoryValidateBtnPressed={isAdditionalMandatoryValidateBtnPressed}
          setIsAdditionalMandatoryValidateBtnPressed={setIsAdditionalMandatoryValidateBtnPressed}
          isAdditionalVoluntary15804ValidateBtnPressed={isAdditionalVoluntary15804ValidateBtnPressed}
          setIsAdditionalVoluntary15804ValidateBtnPressed={setIsAdditionalVoluntary15804ValidateBtnPressed}
          isAdditionalVoluntaryValidateBtnPressed={isAdditionalVoluntaryValidateBtnPressed}
          setIsAdditionalVoluntaryValidateBtnPressed={setIsAdditionalVoluntaryValidateBtnPressed}
          isResource15804ValidateBtnPressed={isResource15804ValidateBtnPressed}
          setIsResource15804ValidateBtnPressed={setIsResource15804ValidateBtnPressed}
          isWaste15804ValidateBtnPressed={isWaste15804ValidateBtnPressed}
          setIsWaste15804ValidateBtnPressed={setIsWaste15804ValidateBtnPressed}
          isOutputFlow15804ValidateBtnPressed={isOutputFlow15804ValidateBtnPressed}
          setIsOutputFlow15804ValidateBtnPressed={setIsOutputFlow15804ValidateBtnPressed}
          customResultButtonPressed={customResultButtonPressed}
          setCustomResultButtonPressed={setCustomResultButtonPressed}
        />
      </TabPanel>
    </TabViewStyled>
  );
};

export default Step3;
